<script setup lang="ts">
import { repository } from "~/repository";
import { first, map, sortBy, chain } from "lodash-es";
import { getThumbnailImage } from "~/utils/common";
import useCollectionAiredData from "~/composables/useCollectionAiredData";
import OnDemandCard from "~/components/On-Demand/OnDemandCard.vue";

useSeoMeta({
  title: "Demand | PokerGO",
  ogTitle: "Demand | PokerGO",
  twitterTitle: "Demand | PokerGO",
});

const { data: response, pending } = await useAsyncData(
  async () => {
    const [listResponse] = await Promise.all([
      repository.common.fetchViewData({
        viewName: "e8562f4a-9ce3-40e5-9966-08176701bf84", // Replace with your second viewName or API call
      }),
    ]);

    const listViewResponse = listResponse.data.collection_list || [];
    return {
      listViewResponse: listViewResponse,
      groups: map(listViewResponse, "title").concat("All Series"),
    };
  },
  {
    lazy: true,
  },
);

const selectedGroup = ref("");
const selectedFilter = ref("Popular");

const filteredItemsList = computed(() => {
  let finalizedList = [];

  if (selectedGroup.value === "All Series") {
    finalizedList = (response.value?.listViewResponse || [])
      .map((item) => item.entities)
      .flat();
  } else {
    finalizedList =
      response.value?.listViewResponse.find(
        (el) => el.title === selectedGroup.value,
      )?.entities || [];
  }
  if (selectedFilter.value === "Popular") {
    return finalizedList;
  } else if (selectedFilter.value === "A-Z") {
    return sortBy(finalizedList, "title");
  } else {
    return finalizedList;
  }
});

watch(
  () => response.value?.groups,
  () => {
    selectedGroup.value = first(response.value?.groups) || "";
  },
  {
    immediate: true,
  },
);

const filterByOptions = ["A-Z", "Popular"];
</script>

<template>
  <div>
    <div class="h-20" />
    <div class="m-auto max-w-screen-xl px-5 py-10">
      <Banner page="on-demand" location="header" />
      <div class="flex flex-col gap-10 lg:flex-row">
        <div class="hidden lg:block">
          <div class="mb-5 text-4xl font-bold">Filters</div>
          <div>
            <div
              v-for="(item, index) in filterByOptions"
              :key="index"
              :class="[
                'flex min-h-14 cursor-pointer items-center text-lg',
                { 'bg-[#9E23D280]': item === selectedFilter },
              ]"
              @click="selectedFilter = item"
            >
              <span :class="[{ 'px-6': item === selectedFilter }]">
                {{ item }}
              </span>
            </div>
          </div>
          <Divider />
          <div class="w-72">
            <div
              v-for="(item, index) in response?.groups"
              :key="index"
              :class="[
                'flex min-h-14 cursor-pointer items-center text-lg',
                { 'bg-[#9E23D280]': item === selectedGroup },
              ]"
              @click="selectedGroup = item"
            >
              <span :class="[{ 'px-6': item === selectedGroup }]">
                {{ item }}
              </span>
            </div>
          </div>
        </div>
        <div>
          <div class="mb-5 text-center text-4xl font-bold lg:text-left">
            {{ selectedGroup || "On Demand" }}
          </div>

          <div v-if="pending" class="mt-20 text-center">
            <ProgressSpinner class="!h-20 !w-20" />
          </div>
          <div v-else>
            <div class="mb-5 flex gap-8 lg:hidden">
              <Dropdown
                v-model="selectedFilter"
                class="w-1/2"
                :options="filterByOptions"
              />
              <Dropdown
                v-model="selectedGroup"
                class="w-1/2"
                scroll-height="400px"
                :options="response?.groups"
              />
            </div>
            <div class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              <OnDemandCard
                v-for="(item, index) in filteredItemsList"
                :key="index"
                :item="item"
                class="hover:bg-primary-900"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
